import {
  isPermitted,
  resolveGrants,
  isRetiredBundle,
  selfAssignableBundles,
  staffBundles,
  type MemberView,
  type PermissionBundle,
  type RoleAssignmentView,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

/** Roles that can be added to an existing member; family roles need an invitation with a person link. */
export const assignableBundles: readonly PermissionBundle[] = staffBundles.filter(
  (bundle) => !isRetiredBundle(bundle),
);

/** The self-assignment form is available only to an active club-wide administrator. */
export function assignableBundlesForMember(
  member: MemberView,
  now: Date,
): readonly PermissionBundle[] {
  if (member.status !== 'active') return [];
  if (!member.isSelf) return assignableBundles;
  const grants = resolveGrants(
    member.assignments.map((assignment) => ({
      ...assignment,
      validFrom: new Date(assignment.validFrom),
      validUntil: assignment.validUntil === null ? null : new Date(assignment.validUntil),
      revokedAt: assignment.revokedAt === null ? null : new Date(assignment.revokedAt),
    })),
    now,
  );
  return isPermitted(grants, 'roles.manage', null) ? selfAssignableBundles : [];
}

export { bundleLabelKeys } from '@stm/i18n';

export type AssignmentStatus = 'active' | 'revoked' | 'expired';

export function assignmentStatus(assignment: RoleAssignmentView, now: Date): AssignmentStatus {
  if (assignment.revokedAt !== null) return 'revoked';
  if (
    assignment.validUntil !== null &&
    new Date(assignment.validUntil).getTime() <= now.getTime()
  ) {
    return 'expired';
  }
  return 'active';
}

export const statusLabelKeys: Readonly<Record<AssignmentStatus, TranslationKey>> = {
  active: 'roles.status.active',
  revoked: 'roles.status.revoked',
  expired: 'roles.status.expired',
};

/** Membership states shown on a member; an unknown state reads as removed, the safest description. */
export function memberStatusLabelKey(status: string): TranslationKey {
  switch (status) {
    case 'active':
      return 'roles.member.status.active';
    case 'suspended':
      return 'roles.member.status.suspended';
    case 'invited':
      return 'roles.member.status.invited';
    default:
      return 'roles.member.status.revoked';
  }
}

/** Names do not exist until the people module; the last characters tell members apart. */
export function shortIdentifier(identifier: string): string {
  return identifier.slice(-6);
}

/** The end date is a whole day: the role works through it, in the browser's time zone. */
export function endOfDayIso(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const end = new Date(`${date}T23:59:59`);
  return Number.isNaN(end.getTime()) ? null : end.toISOString();
}
